/* eslint-disable no-unused-vars */
/* eslint-disable indent */
import React, { useState, useEffect } from "react";
import { useRequestCancellation } from "../../../../hooks/useRequestCancellation";
import { Card, Row, Col, Button, message } from "antd";
import { DownloadOutlined } from "@ant-design/icons";

import {
  DropDownComponent,
  TableComponent,
  CalendarComponent,
  RefreshTimestamp
} from "../../CommonComponents";
import "../styles.scss";
import Heading from "../../../../components/Heading";
import { ReportsService } from "../../../../services/api/reports";
import { getHubList, getHubCPList } from "../../../../services/api/hub";
import moment from "moment";
import { downloadBookedVsAuditedReport } from "../../../../utils/downloadUtils";
import { useNavigate } from "react-router-dom";


const BookedVsAuditedReport = () => {
  const FILTER_STORAGE_KEY = "BOOKED_VS_AUDITED_REPORT_FILTERS";

  // Global request cancellation hook
  const { createNewRequest, isLatestRequest } = useRequestCancellation();

  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [dateRange, setDateRange] = useState([
    moment().subtract(1, "days"),
    moment()
  ]);
  const [reportData, setReportData] = useState([]);
  const [hubList, setHubList] = useState([]);
  const [selectedHubs, setSelectedHubs] = useState([]);
  const [hubLoading, setHubLoading] = useState(false);
  const [cpList, setCpList] = useState([]);
  const [selectedCps, setSelectedCps] = useState([]);
  const [cpLoading, setCpLoading] = useState(false);
  const [docTypeList] = useState([
    { label: "Dox", value: "DOX" },
    { label: "Non-dox", value: "NON-DOX" }
  ]);
  const [selectedDocTypes, setSelectedDocTypes] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
  });
  const [hubUser, setHubUser] = useState();
  const navigate = useNavigate();
  const [lastsynced, setLastsynced] = useState(null);
  const [nextScheduledRefresh, setNextScheduledRefresh] = useState(null);
  const [pendingCpSelection, setPendingCpSelection] = useState(null); // Store CP selection to restore after CP list loads

  // Helper function to save filters to sessionStorage
  const saveFiltersToStorage = () => {
    const filters = {
      dateRange: dateRange ? [
        dateRange[0]?.format("YYYY-MM-DD"),
        dateRange[1]?.format("YYYY-MM-DD")
      ] : null,
      selectedHubs,
      selectedCps,
      selectedDocTypes,
      pagination: {
        current: pagination.current,
        pageSize: pagination.pageSize
      }
    };
    sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
  };

  // Helper function to restore filters from sessionStorage
  const restoreFiltersFromStorage = () => {
    try {
      const savedFilters = sessionStorage.getItem(FILTER_STORAGE_KEY);
      if (savedFilters) {
        const filters = JSON.parse(savedFilters);
        if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
          setDateRange([
            moment(filters.dateRange[0]),
            moment(filters.dateRange[1])
          ]);
        }
        if (filters.selectedHubs && filters.selectedHubs.length > 0) {
          setSelectedHubs(filters.selectedHubs);
        }
        // Store CP selection to restore after CP list is loaded
        if (filters.selectedCps && filters.selectedCps.length > 0) {
          setPendingCpSelection(filters.selectedCps);
        }
        if (filters.selectedDocTypes && filters.selectedDocTypes.length > 0) {
          setSelectedDocTypes(filters.selectedDocTypes);
        }
        if (filters.pagination) {
          setPagination(prev => ({
            ...prev,
            current: filters.pagination.current || prev.current,
            pageSize: filters.pagination.pageSize || prev.pageSize
          }));
        }
        // Clear the saved filters after restoring
        sessionStorage.removeItem(FILTER_STORAGE_KEY);
        return true;
      }
    } catch (error) {
      console.error("Error restoring filters:", error);
    }
    return false;
  };


  // Initial load - only fetch hub list
  useEffect(() => {
    fetchHubList();
  }, []);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("userinfo") || "{}");
    let userid = JSON.parse(localStorage.getItem("userId") || "{}");
    setHubUser(user?.roles?.[0]);

    const activeHub = JSON.parse(localStorage.getItem("active_hub"));
    const activeHubId = activeHub?.hubId;

    // Check if there are saved filters - if yes, don't set default hub (restore will handle it)
    const hasSavedFilters = sessionStorage.getItem(FILTER_STORAGE_KEY);
    // Only set default hub if no hubs are selected and no filters were saved (to avoid overriding restored filters)
    if (hubList.length > 0 && selectedHubs.length === 0 && !hasSavedFilters) {
      if (activeHubId) {
        const defaultHub = hubList.find((hub) => String(hub.value) === String(activeHubId));
        if (defaultHub) {
          setSelectedHubs([defaultHub.value]);
          selectHubHandle([defaultHub.value]);
        }
      } else if (user?.roles?.[0] === "ROLE_HUBADMIN") {
        const defaultHub = hubList.find(hub => String(hub.value) === String(userid?.userInfo?.premiseId));
        if (defaultHub) {
          setSelectedHubs([defaultHub.value]);
          selectHubHandle([defaultHub.value]);
        }
      }
    }
  }, [hubList]);

  // Fetch CP list when hubs are selected
  useEffect(() => {
    if (selectedHubs.length === 0) {
      setCpList([]);
      setSelectedCps([]);
      setPendingCpSelection(null); // Clear pending CP selection
      return;
    }

    (async () => {
      try {
        setCpLoading(true);
        const cpLists = await Promise.all(selectedHubs.map(hubId => getHubCPList(hubId)));
        const allCps = [...new Set(cpLists.flat())];
        setCpList(allCps);
      } catch (error) {
        console.error("Failed to fetch CP list", error);
        message.error("Failed to fetch CP list");
        setCpList([]);
      } finally {
        setCpLoading(false);
      }
    })();
  }, [selectedHubs]);

  // Restore CP selection after CP list is loaded
  useEffect(() => {
    if (pendingCpSelection && cpList.length > 0) {
      // Filter to only include CPs that exist in the current CP list
      const validCpIds = pendingCpSelection.filter(cpId =>
        cpList.some(cp => cp.value === cpId)
      );
      if (validCpIds.length > 0) {
        setSelectedCps(validCpIds);
      }
      setPendingCpSelection(null); // Clear pending selection after restoring
    }
  }, [cpList, pendingCpSelection]);

  // Debounced effect for report fetching
  useEffect(() => {
    if (!hubUser) return; // Wait for hubUser to be set
    if (hubUser === "ROLE_HUBADMIN" && selectedHubs.length === 0) return; // Only block for hubadmin
    if (!dateRange[0] || !dateRange[1]) return; // Always require date range
    // If CPs are selected, wait for CP list to be loaded
    if (selectedCps && selectedCps.length > 0 && cpList.length === 0 && selectedHubs.length > 0) {
      return; // Wait for CP list to load
    }
    const timer = setTimeout(() => {
      fetchBookedVsAuditedReport();
    }, 300);

    return () => clearTimeout(timer);
  }, [hubUser, selectedHubs, selectedCps, selectedDocTypes, dateRange, pagination.current, pagination.pageSize, cpList]);

  const handleTableChange = (newPagination) => {
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize
    }));
  };

  const columns = [
    {
      title: "No.",
      dataIndex: "index",
      key: "index",
      width: 60,
      render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      sorter: true,
    },
    {
      title: "Hub Name",
      dataIndex: "hubName",
      key: "hubName",
      sorter: true,
    },
    {
      title: "CP Name",
      dataIndex: "cpName",
      key: "cpName",
      sorter: true,
    },
    {
      title: "Zone",
      dataIndex: "zone",
      key: "zone",
      sorter: true,
      width: 120,
    },
    {
      title: "State",
      dataIndex: "state",
      key: "state",
      sorter: true,
      width: 120,
    },
    {
      title: "Document Type",
      dataIndex: "doctype",
      key: "doctype",
      sorter: true,
      render: (text) => text?.toUpperCase() || "-",
      width: 140,
    },
    {
      title: "Total Bookings",
      dataIndex: "totalBookings",
      key: "totalBookings",
      sorter: true,
      width: 120,
      render: (text, record) => (
        text && text > 0 ? (
          <span
            style={{
              color: "#1890ff",
              cursor: "pointer",
              textDecoration: "underline",
              fontWeight: "bold"
            }}

            onClick={() => {
              // Save current filters before navigating
              saveFiltersToStorage();

              navigate("/total-bookings-audit-report", {
                state: {
                  bookingDate: record.date,
                  hubId: record.hubId,
                  cpId: record.cpId,
                  docType: record.doctype
                }
              });
            }}
          >
            {text}
          </span>
        ) : <span>{text || 0}</span>
      ),
    },
    {
      title: "Total Audited",
      dataIndex: "totalAudited",
      key: "totalAudited",
      sorter: true,
      width: 120,
      render: (text, record) => (
        text && text > 0 ? (
          <span
            style={{
              color: "#1890ff",
              cursor: "pointer",
              textDecoration: "underline",
              fontWeight: "bold"
            }}
            onClick={() => {
              // Save current filters before navigating
              saveFiltersToStorage();

              navigate("/audited-details-report", {
                state: {
                  bookingDate: record.date,
                  hubId: record.hubId,
                  cpId: record.cpId,
                  docType: record.doctype
                }
              });
            }}
          >
            {text}
          </span>
        ) : <span>{text || 0}</span>
      ),
    },
    {
      title: "Weight Difference (kg)",
      dataIndex: "weightDifference",
      key: "weightDifference",
      sorter: true,
      width: 140,
    },
  ];

  const selectHubHandle = (values) => {
    setSelectedHubs(values || []); // Always allow updating selectedHubs
    setSelectedCps([]); // Clear CP selection when hubs change
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const selectCpHandle = (values) => {
    setSelectedCps(values || []);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleDocTypeChange = (values) => {
    setSelectedDocTypes(values || []);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates || []); // Handle undefined when cleared
  };

  const handleDownload = async () => {
    if (!dateRange[0] || !dateRange[1]) {
      message.error("Please select a date range for download");
      return;
    }

    {/*if (selectedHubs.length === 0) {
      message.error("Please select at least one hub for download");
      return;
    }*/}


    setDownloadLoading(true);
    try {
      const selectedHubData = hubList.filter(hub => selectedHubs?.includes(hub.value));
      const selectedCpData = cpList.filter(cp => selectedCps?.includes(cp.value));

      const downloadParams = {
        ...(selectedHubData?.length > 0 && { hubNames: selectedHubData.map(hub => hub.label) }),
        ...(selectedHubData?.length > 0 && { hubIds: selectedHubData.map(hub => hub.value) }),
        ...(selectedCpData?.length > 0 && { cpNames: selectedCpData.map(cp => cp.label) }),
        ...(selectedCpData?.length > 0 && { cpIds: selectedCpData.map(cp => cp.value) }),
        ...(selectedDocTypes?.length > 0 && { docTypes: selectedDocTypes.join(",") }),
        fromDate: dateRange[0] ? dateRange[0].format("YYYY-MM-DD") : "",
        toDate: dateRange[1] ? dateRange[1].format("YYYY-MM-DD") : ""
      };

      //console.log("downloadParams", downloadParams);
      await downloadBookedVsAuditedReport(downloadParams);
      message.success("Report downloaded successfully");
    } catch (error) {
      console.error("Download error:", error);
      message.error("Failed to download report");
    } finally {
      setDownloadLoading(false);
    }
  };

  // All API Call

  //Get Hub List
  const fetchHubList = async () => {
    setHubLoading(true);
    try {
      const hubData = await getHubList();
      setHubList(hubData);
      // Restore filters after hub list is loaded
      restoreFiltersFromStorage();
    } catch (error) {
      message.error("Failed to fetch hub list");
      setHubList([]);
    } finally {
      setHubLoading(false);
    }
  };
  //this for fetch reprot
  const fetchBookedVsAuditedReport = async () => {
    // Only block for hubadmin with no hub or if date range is not set
    const user = JSON.parse(localStorage.getItem("userinfo"));
    const isHubAdmin = user?.roles?.[0] === "ROLE_HUBADMIN";
    if (!dateRange[0] || !dateRange[1] || (isHubAdmin && selectedHubs.length === 0)) {
      setReportData([]);
      setPagination(prev => ({ ...prev, total: 0 }));
      return;
    }

    // 🔥 CREATE NEW REQUEST WITH GLOBAL HOOK
    const { signal, requestId } = createNewRequest("booked-vs-audited-report");

    try {
      setLoading(true);

      const selectedHubData = hubList.filter(hub => selectedHubs?.includes(hub.value));
      const selectedCpData = cpList.filter(cp => selectedCps?.includes(cp.value));
      // Only include non-empty arrays in params
      const params = {
        ...(selectedHubData?.length > 0 && { hubIds: selectedHubData.map(hub => hub.value) }),
        ...(selectedCpData?.length > 0 && { cpIds: selectedCpData.map(cp => cp.value) }),
        ...(selectedDocTypes?.length > 0 && { docTypes: selectedDocTypes.join(",") }),
        ...(dateRange?.[0] && dateRange?.[1] && {
          fromDate: dateRange[0].format("YYYY-MM-DD"),
          toDate: dateRange[1].format("YYYY-MM-DD")
        }),
        page: pagination.current,
        pageSize: pagination.pageSize
      };

      const { data, total, page, pageSize, lastRefreshedAt, nextScheduledRefresh } = await ReportsService.getBookedVsAuditedReport(params, {
        signal
      });

      // 🔥 ONLY UPDATE IF THIS IS THE LATEST REQUEST
      if (isLatestRequest(requestId)) {
        console.log(`🔥 Request ${requestId} completed successfully - updating state`);
        setReportData(data);
        setLastsynced(lastRefreshedAt);
        setNextScheduledRefresh(nextScheduledRefresh);
        setPagination(prev => ({
          ...prev,
          total,
        }));
      }

    } catch (error) {
      // 🔥 DON"T UPDATE STATE IF REQUEST WAS CANCELLED
      if (error.name === "AbortError") {
        console.log("🔥 Request was cancelled at HTTP level - no state update");
        return;
      }

      // Only update if this is still the latest request
      if (isLatestRequest(requestId)) {
        message.error("Failed to fetch report data");
        setReportData([]);
        setPagination(prev => ({
          ...prev,
          total: 0
        }));
      }
    } finally {
      // 🔥 ONLY UPDATE LOADING IF THIS IS THE LATEST REQUEST
      if (isLatestRequest(requestId)) {
        setLoading(false);
      }
    }
  };

  return (
    <div className="report-container">
      <div style={{ backgroundColor: "white", padding: "8px 16px", borderRadius: "8px" }}>
        <Heading
          title="Booked vs Audited Report"
          navigateTo="/reports"
        >
          <div style={{ paddingTop: "5px" }}>
            <RefreshTimestamp
              lastRefreshedAt={lastsynced}
              nextScheduledRefresh={nextScheduledRefresh}
            />
          </div>
        </Heading>
      </div >
      <Card className="report-card">
        <Row gutter={[16, 16]} className="filters-section">
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-label">From & To Date</div>
            <CalendarComponent
              className="custom-calendar"
              type="range"
              value={dateRange}
              onChange={handleDateRangeChange}
              format="YYYY-MM-DD"
              allowClear
            />
          </Col>

          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-label">Select Hubs</div>
            <DropDownComponent
              className="small-dropdown"
              placeholder="From Hub"
              options={hubList}
              onChange={(value) => {
                selectHubHandle(value);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              value={selectedHubs}
              allowClear
              mode={hubUser !== "ROLE_HUBADMIN" ? "multiple" : "default"}
              disabled={hubUser === "ROLE_HUBADMIN"}
              maxTagCount="responsive"
              showSearch={true}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label || "").toLowerCase().includes(input.toLowerCase())
              }
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-label">Select CPs</div>
            <DropDownComponent
              className="small-dropdown"
              placeholder="Select CPs"
              options={cpList}
              onChange={selectCpHandle}
              value={selectedCps}
              loading={cpLoading}
              disabled={selectedHubs?.length === 0}
              allowClear
              mode="multiple"
              maxTagCount="responsive"
              showSearch={true}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              dropdownStyle={{ maxHeight: 256, overflow: "auto" }}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-label">Document Types</div>
            <DropDownComponent
              placeholder="Document Types"
              options={docTypeList}
              onChange={handleDocTypeChange}
              value={selectedDocTypes}
              allowClear
              mode="multiple"
              maxTagCount="responsive"
              dropdownStyle={{ maxHeight: 256, overflow: "auto" }}
            />
          </Col>
        </Row>
        <Row style={{ marginTop: 16 }}>
          <Col span={24} style={{ textAlign: "right" }}>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              loading={downloadLoading}
              onClick={handleDownload}
              disabled={!dateRange[0] || !dateRange[1]}
            >
              Download Report
            </Button>
          </Col>
        </Row>
      </Card>
      <TableComponent
        columns={columns}
        dataSource={reportData}
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        scroll={{ y: "calc(100vh - 300px)", x: "max-content" }}
        sticky
      />
    </div >
  );
};

export default BookedVsAuditedReport;

